import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Warning, Clock } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const fmtDur = (s) => (s == null ? "—" : `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, "0")} s`);

/** Résultats d'un quiz pour le professeur : notes, durée, changements d'onglet, temps écoulé. */
export default function QuizResults({ quiz, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/quizzes/${quiz.id}/results`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))); }, [quiz.id]);
  const doneRows = data ? data.rows.filter((r) => r.done) : [];
  const avg = doneRows.length ? Math.round(doneRows.reduce((a, r) => a + r.pct, 0) / doneRows.length) : null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="quiz-results">
        <DialogHeader>
          <DialogTitle>Résultats : {quiz.title}</DialogTitle>
          <DialogDescription>
            {data ? `${doneRows.length} étudiant(s) sur ${data.rows.length} ont passé le quiz${avg !== null ? ` · moyenne ${avg} %` : ""}` : "Chargement…"}
          </DialogDescription>
        </DialogHeader>
        {data && (
          <div className="max-h-[55vh] overflow-auto rounded-lg border border-border">
            <table className="w-full min-w-[560px] text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                <tr><th className="p-2">Étudiant</th><th className="p-2">Note</th><th className="p-2">Durée</th><th className="p-2">Sorties de page</th><th className="p-2">Remarque</th></tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.student_id} className="border-t border-border">
                    <td className="p-2 font-medium">{r.name}</td>
                    <td className="p-2">{r.done ? `${r.score}/${r.total} (${r.pct} %)` : <span className="text-muted-foreground">Pas passé</span>}</td>
                    <td className="p-2 text-muted-foreground">{fmtDur(r.time_taken_seconds)}</td>
                    <td className="p-2">
                      {r.done ? (r.tab_switches > 0
                        ? <span className="inline-flex items-center gap-1 text-amber-700"><Warning size={14} weight="fill" /> {r.tab_switches}</span>
                        : <span className="text-muted-foreground">0</span>) : "—"}
                    </td>
                    <td className="p-2">{r.timed_out && <Badge variant="outline" className="text-accent"><Clock size={12} className="mr-1" /> Temps écoulé</Badge>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-xs text-muted-foreground">« Sorties de page » compte les fois où l'étudiant a quitté la fenêtre du quiz (autre onglet, autre application). Un nombre élevé est un signal à vérifier, pas une preuve.</p>
        <DialogFooter><Button onClick={onClose}>Fermer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
